// 🔒 DGII-RNC-VALIDATOR-ACCOUNTING-2026-07
import { useState, useEffect, useRef } from 'react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Hook para validar RNC/cédula contra DGII (API Megaplus).
 *
 * Uso:
 *   const { status, data, isChecking, isValid, documentType } = useRncValidator(rncValue);
 *
 *   status: 'idle' | 'invalid_format' | 'checking' | 'valid' | 'not_found' | 'error'
 *   data: { valid, rnc, nombre, nombre_comercial, estado, actividad_economica, ... } | null
 *   isChecking: boolean (true durante la request)
 *   isValid: boolean (true cuando DGII devolvió valid=true)
 *   documentType: 'RNC' (9 digs) | 'CEDULA' (11 digs) — inferido del input
 *
 * Debounce 400ms para no golpear la API al escribir.
 * Cache in-memory del lado del cliente (además del cache en backend).
 * Silent fail: cualquier error de red se refleja como status='error' sin bloquear la UI.
 */
const _clientCache = new Map();

export function useRncValidator(rncValue) {
  const [status, setStatus] = useState('idle');
  const [data, setData] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    // Limpiar timer anterior
    if (timerRef.current) clearTimeout(timerRef.current);

    // Normalizar (quitar guiones/espacios/letras)
    const clean = (rncValue || '').replace(/\D/g, '');

    // Vacío → idle
    if (!clean) {
      setStatus('idle');
      setData(null);
      return;
    }

    // Formato inválido → no llamar API
    if (clean.length !== 9 && clean.length !== 11) {
      setStatus('invalid_format');
      setData(null);
      return;
    }

    // Cache hit → resolver inmediato
    if (_clientCache.has(clean)) {
      const cached = _clientCache.get(clean);
      setStatus(cached.valid ? 'valid' : 'not_found');
      setData(cached);
      return;
    }

    // Debounce 400ms + llamada API
    setStatus('checking');
    timerRef.current = setTimeout(async () => {
      try {
        const res = await fetch(
          `${API_BASE}/api/dgii/validate-rnc/${clean}`,
          { headers: { Authorization: `Bearer ${localStorage.getItem('pos_token')}` } }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const result = await res.json();
        // 🔒 AVISO-RNC-2026-10-29 — Solo se guarda lo encontrado, y «no encontrado» es solo cuando la
        // DGII lo dijo (no_encontrado). Un timeout o un error de Megaplus se muestra como error.
        if (result.valid) _clientCache.set(clean, result);
        setData(result);
        setStatus(result.valid ? 'valid' : result.no_encontrado ? 'not_found' : 'error');
      } catch (err) {
        // Silent fail: si DGII no responde, la UI sigue operando.
        setStatus('error');
        setData(null);
      }
    }, 400);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [rncValue]);

  return {
    status,
    data,
    isChecking: status === 'checking',
    isValid: status === 'valid',
    documentType: (rncValue || '').replace(/\D/g, '').length === 11 ? 'CEDULA' : 'RNC',
  };
}

export default useRncValidator;
